import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Paperclip, Send } from 'lucide-react';
import type { TranscriptEntry } from './types';

interface KatiaChatProps {
  transcript: TranscriptEntry[];
  sending: boolean;
  locked: boolean; // true once the brief is complete — no more input
  onSend: (text: string) => void;
  // Upload a PDF casting brief — parsed server-side into the structured state.
  onUploadBrief?: (file: File) => void;
}

// Katia reads as a person: her name heads her messages, never "AI assistant".
export const KatiaChat: React.FC<KatiaChatProps> = ({ transcript, sending, locked, onSend, onUploadBrief }) => {
  const [draft, setDraft] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const takeFile = (f?: File | null) => {
    if (!f || !onUploadBrief) return;
    if (!/\.pdf$/i.test(f.name)) return;
    onUploadBrief(f);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [transcript, sending]);

  const submit = () => {
    const t = draft.trim();
    if (!t || sending || locked) return;
    onSend(t);
    setDraft('');
  };

  return (
    <div
      className="flex flex-col"
      style={{
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-surface-1)',
        border: '1px solid var(--hj-hairline)',
        minHeight: 440,
        maxHeight: 620,
      }}
    >
      <div
        className="flex items-center gap-2.5"
        style={{ padding: '14px 18px', borderBottom: '1px solid var(--hj-hairline-soft)' }}
      >
        <div
          className="rounded-full flex items-center justify-center flex-shrink-0"
          style={{ width: 32, height: 32, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontWeight: 800, fontFamily: 'var(--hj-font-mono)', fontSize: 13 }}
        >
          K
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.1 }}>Katia</div>
          <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Casting</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto flex flex-col gap-3" style={{ padding: '18px' }}>
        {transcript.map((m, i) => (
          <div
            key={i}
            className="flex flex-col"
            style={{ alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}
          >
            <div
              style={{
                maxWidth: '82%',
                padding: '10px 13px',
                borderRadius: 12,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.5,
                background: m.role === 'user' ? 'var(--hj-yellow)' : 'var(--hj-surface-2)',
                color: m.role === 'user' ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
                border: m.role === 'user' ? 'none' : '1px solid var(--hj-hairline-soft)',
              }}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2" style={{ color: 'var(--hj-ink-3)', fontSize: 13 }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Katia is typing…
          </div>
        )}
      </div>

      <div style={{ padding: '12px 14px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
        {locked ? (
          <p className="m-0 text-center" style={{ fontSize: 13, color: 'var(--hj-ink-3)' }}>
            Your brief is complete.
          </p>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Type your answer…"
              rows={1}
              disabled={sending}
              className="flex-1 resize-none"
              style={{
                background: 'var(--hj-surface-2)',
                border: '1px solid var(--hj-hairline)',
                borderRadius: 10,
                color: 'var(--hj-ink)',
                fontSize: 14,
                padding: '10px 12px',
                fontFamily: 'inherit',
                maxHeight: 120,
                minHeight: 42,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={submit}
              disabled={sending || !draft.trim()}
              aria-label="Send"
              className="inline-flex items-center justify-center flex-shrink-0"
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                background: 'var(--hj-yellow)',
                color: 'oklch(0.18 0.005 70)',
                border: 'none',
                cursor: sending || !draft.trim() ? 'not-allowed' : 'pointer',
                opacity: sending || !draft.trim() ? 0.5 : 1,
              }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Drop zone — the obvious way in for creators who already have a
            brief. Drag a PDF anywhere onto it, or click to browse. */}
        {onUploadBrief && !locked && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => { takeFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer.files?.[0]); }}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2"
              style={{
                marginTop: 10,
                padding: '13px 16px',
                borderRadius: 10,
                background: dragOver ? 'var(--hj-surface-2)' : 'transparent',
                border: dragOver ? '2px dashed var(--hj-yellow)' : '2px dashed var(--hj-hairline)',
                color: 'var(--hj-ink)',
                fontSize: 13,
                fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                opacity: sending ? 0.5 : 1,
              }}
            >
              <Paperclip className="w-4 h-4" style={{ color: 'var(--hj-yellow-ink)' }} />
              Already have your project details? Drop a PDF here — Katia reads it and fills in your brief
            </button>
          </>
        )}
      </div>
    </div>
  );
};
